import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';
import useModalDialog from '../hooks/useModalDialog';

// Full-screen lightbox showing the uncropped original(s). Supports a single
// image or a before/after pair. Closes on Esc, backdrop click, or the X button.
//
// Navigation: users can page through the full projects list with the arrow
// buttons (visible when there are 2+ items) or Left/Right arrow keys.
//
// Rendered via a portal to `document.body` so it escapes any ancestor stacking
// context (transform / filter / will-change) and always sits above the sticky
// site header on iOS Safari.
const HINT_KEY = 'ff-lb-hint-seen';

const Lightbox = ({ projects, startIndex = 0, onClose }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const swipeRef = useRef({ x: 0, y: 0, active: false });
  const [showHint, setShowHint] = useState(false);
  const [idx, setIdx] = useState(startIndex);

  const n = projects ? projects.length : 0;
  const canNavigate = n > 1;
  const project = n > 0 ? projects[idx] : null;
  const isOpen = Boolean(project);

  const goPrev = useCallback(() => {
    if (!canNavigate) return;
    setIdx((i) => (i - 1 + n) % n);
  }, [n, canNavigate]);
  const goNext = useCallback(() => {
    if (!canNavigate) return;
    setIdx((i) => (i + 1) % n);
  }, [n, canNavigate]);

  useModalDialog({ open: isOpen, dialogRef, initialFocusRef: closeRef, onClose,
    onPrevious: canNavigate ? goPrev : undefined, onNext: canNavigate ? goNext : undefined });

  useEffect(() => {
    if (!isOpen) return;
    setShowHint(false);
    // One-time "Tap outside to close" hint on touch/coarse-pointer devices.
    // Same flag also drives the one-shot pulse on the Close X.
    let fadeTimer;
    try {
      const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
      const seen = window.localStorage.getItem(HINT_KEY) === '1';
      if (isTouch && !seen) {
        setShowHint(true);
        window.localStorage.setItem(HINT_KEY, '1');
        fadeTimer = setTimeout(() => setShowHint(false), 2600);
      }
    } catch (_) { /* localStorage unavailable — silently skip */ }

    return () => {
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [isOpen]);

  if (!project) return null;
  const ba = project.beforeAfter;

  // Touch swipe handlers (mobile). Only bound on the plain-image case —
  // never on the BeforeAfterSlider, which owns its own horizontal drag.
  const onTouchStart = (e) => {
    if (!canNavigate || e.touches.length !== 1) return;
    const t = e.touches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    const s = swipeRef.current;
    if (!s.active) return;
    s.active = false;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    // Horizontal swipe only: ≥ 50px along X, and X-dominant vs Y.
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goNext(); else goPrev();
  };

  return createPortal(
    <div
      ref={dialogRef}
      className="lightbox"
      role="dialog"
      tabIndex={-1}
      aria-modal="true"
      aria-label={`${project.caption} — full image`}
      data-testid="lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <button
        ref={closeRef}
        type="button"
        className={`lightbox-close${showHint ? ' lightbox-close--pulse' : ''}`}
        aria-label="Close image"
        data-testid="lightbox-close"
        onClick={onClose}
      >
        <X size={28} strokeWidth={2.4} aria-hidden="true" />
      </button>

      {canNavigate && (
        <>
          <button
            type="button"
            className="lightbox-nav lightbox-nav--prev"
            aria-label="Previous photo"
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
          >
            <ChevronLeft size={30} strokeWidth={2.4} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="lightbox-nav lightbox-nav--next"
            aria-label="Next photo"
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
          >
            <ChevronRight size={30} strokeWidth={2.4} aria-hidden="true" />
          </button>
          <span
            className="lightbox-counter"
            aria-hidden="true"
            data-testid="lightbox-counter"
          >
            {idx + 1} / {n}
          </span>
        </>
      )}

      <div className="lightbox-body" onClick={(e) => e.stopPropagation()} key={project.id}>
        {ba ? (
          <BeforeAfterSlider before={ba.before} after={ba.after} />
        ) : (
          <picture
            className="lightbox-swipe"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <source type="image/webp" srcSet={project.full.webp} />
            <img src={project.full.jpg} alt={project.alt} className="lightbox-img" data-testid="lightbox-img" />
          </picture>
        )}
        <p className="lightbox-caption">{project.caption}</p>
      </div>
      {showHint && (
        <div
          className="lightbox-hint"
          role="status"
          aria-live="polite"
          data-testid="lightbox-hint"
        >
          Tap outside to close
        </div>
      )}
    </div>,
    document.body
  );
};

export default Lightbox;
